'use client';

import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';
import type { LegalDoc } from '@/lib/legal/types';
import { useI18n } from '@/lib/i18n';

/** Renders a structured legal document (Privacy Policy / Terms). Public page. */
export function LegalDocPage({ doc }: { doc: LegalDoc }) {
  const { t } = useI18n();

  return (
    <main className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-5 py-10 lg:py-14">
        <div className="flex items-center gap-3 mb-6">
          <Link href="/" className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" aria-label={t('customerProfile.goBack')}>
            <ArrowLeft size={18} className="text-gray-600" />
          </Link>
          <span className="font-serif font-medium text-2xl  text-gray-900">Bevelure</span>
        </div>

        <h1 className="font-serif font-medium text-3xl sm:text-4xl  text-gray-900 mb-4">{doc.title}</h1>

        {doc.intro.split('\n').filter((p) => p.trim()).map((p, i) => (
          <p key={i} className="text-sm text-gray-600 leading-relaxed mb-3 whitespace-pre-wrap">{p}</p>
        ))}

        <div className="mt-8 space-y-8">
          {doc.sections.map((s, si) => (
            <section key={si}>
              <h2 className="font-semibold text-lg text-gray-900 mb-2">{s.heading}</h2>
              <div className="space-y-2">
                {(() => {
                  // Group consecutive "• " lines into a single <ul>.
                  const out: React.ReactNode[] = [];
                  let bullets: string[] = [];
                  const flush = (key: string) => {
                    if (!bullets.length) return;
                    out.push(
                      <ul key={`ul-${key}`} className="list-disc pl-5 space-y-1">
                        {bullets.map((b, bi) => (
                          <li key={bi} className="text-sm text-gray-700 leading-relaxed">{b.replace(/^•\s*/, '')}</li>
                        ))}
                      </ul>
                    );
                    bullets = [];
                  };
                  s.body.forEach((para, pi) => {
                    if (para.trimStart().startsWith('•')) {
                      bullets.push(para.trimStart());
                    } else {
                      flush(`${si}-${pi}`);
                      out.push(
                        <p key={pi} className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{para}</p>
                      );
                    }
                  });
                  flush(`${si}-end`);
                  return out;
                })()}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-gray-100 flex gap-4 text-sm">
          <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="text-customer-600 hover:underline">{t('nav.privacy')}</Link>
          <Link href="/terms" target="_blank" rel="noopener noreferrer" className="text-customer-600 hover:underline">{t('nav.terms')}</Link>
        </div>
      </div>
    </main>
  );
}
